// ============================================================
// fireModes - what one trigger pull costs.
//
// The Storm/Twin-Linked/Maximal ammo maths extracted from
// useAttackExecution (v8.234.0) so the fire selector's tooltips
// and the actual ammo check read the SAME numbers - a tooltip
// computed separately would drift, the way forecasts used to.
// ============================================================
import { hasWS, getWSP } from './weaponSpecials';
import type { Slot } from '../types';
import { hasCondition } from './conditions';

/** Fire-mode keys that consume ammunition. Melee modes cost nothing. */
const RANGED_MODES = new Set(['single', 'semi', 'full']);

/**
 * Rounds spent by ONE pull of the trigger in the given fire mode.
 * Fallbacks (semi 3 / full 10) mirror the attack path's own guards.
 * Official ammo multipliers stack multiplicatively: Storm fires two
 * rounds per shot, Twin-Linked feeds two barrels, Maximal vents three
 * charges.
 */
export function ammoCostPerTrigger(slot: Slot, fireModeKey: string): number {
  if (slot.weaponType === 'melee' || !RANGED_MODES.has(fireModeKey)) return 0;
  const sp = slot.specialRules || [];
  const cost = roundsPerMode(slot, fireModeKey) * ammoMultiplier(sp);
  // Guzzling/Frugal (v8.282.0): multiply first, divide last, floor at one
  // round - a trigger pull is never free.
  const frugal = getWSP(sp, 'Frugal');
  return frugal > 1 ? Math.max(1, Math.floor(cost / frugal)) : cost;
}

/** Rounds one pull fires before the multipliers; fallbacks semi 3 / full 10. */
function roundsPerMode(slot: Slot, fireModeKey: string): number {
  if (fireModeKey === 'single') return 1;
  return fireModeKey === 'semi' ? slot.rateOfFire.semi || 3 : slot.rateOfFire.full || 10;
}

/** Maximal ×3, Storm ×2, Twin-Linked ×2, Guzzling(X) ×X - they stack multiplicatively. */
function ammoMultiplier(sp: string[]): number {
  let mult = 1;
  if (hasWS(sp, 'Maximal')) mult *= 3;
  if (hasWS(sp, 'Storm')) mult *= 2;
  if (hasWS(sp, 'TwinLinked')) mult *= 2;
  const guzzling = getWSP(sp, 'Guzzling');
  return guzzling > 1 ? mult * guzzling : mult;
}

/**
 * Cycling(X) (v8.289.0): rounds of recharge a trigger pull demands.
 * A bare classic `Recharge` reads as Cycling(1) - the old prose rule,
 * finally enforced. 0 = no cooldown.
 */
export function cyclingRounds(specialRules: string[]): number {
  const cycling = getWSP(specialRules, 'Cycling');
  if (cycling > 0) return cycling;
  return hasWS(specialRules, 'Recharge') ? 1 : 0;
}

/** One round passes: every cooldown steps down, expired entries drop. */
export function tickCooldowns(cooldowns: Record<string, number>): Record<string, number> {
  const next: Record<string, number> = {};
  for (const [id, rounds] of Object.entries(cooldowns)) {
    if (rounds > 1) next[id] = rounds - 1;
  }
  return next;
}

/** Human notes for the active multipliers, in application order. */
export function ammoMultiplierNotes(slot: Slot): string[] {
  const sp = slot.specialRules || [];
  const notes: string[] = [];
  if (hasWS(sp, 'Maximal')) notes.push('x3 Maximal');
  if (hasWS(sp, 'Storm')) notes.push('x2 Storm');
  if (hasWS(sp, 'TwinLinked')) notes.push('x2 Twin-Linked');
  const guzzling = getWSP(sp, 'Guzzling');
  if (guzzling > 1) notes.push(`x${guzzling} Guzzling`);
  const frugal = getWSP(sp, 'Frugal');
  if (frugal > 1) notes.push(`÷${frugal} Frugal`);
  return notes;
}

/**
 * Why this fire mode is refused for a shooter in these conditions (2026-10-26), or null. Pinned fires single shots
 * only (snap fire); a runner fires full auto only - and a weapon with no full auto cannot fire at all while he ran.
 * Ranged only: neither limits a melee strike. The trigger refuses through `attackBlockedReason`; the selector dims.
 */
export function fireModeRefusal(
  conds: readonly string[] | null | undefined,
  modeKey: string,
  slot: Pick<Slot, 'weaponType' | 'rateOfFire'> | null | undefined,
): string | null {
  if (!slot || slot.weaponType !== 'ranged') return null;
  if (hasCondition(conds, 'Pn') && modeKey !== 'single') return pinnedWords(slot);
  if (hasCondition(conds, 'Rn') && modeKey !== 'full') return ranWords(slot);
  return null;
}

// Each says so when the weapon cannot meet the limit at all, rather than naming a mode it does not have.
const pinnedWords = (slot: Pick<Slot, 'rateOfFire'>) =>
  slot.rateOfFire?.single
    ? 'Pinned: single shot only (snap fire).'
    : 'Pinned: single shot only (snap fire), and this weapon has none.';
const ranWords = (slot: Pick<Slot, 'rateOfFire'>) =>
  (slot.rateOfFire?.full ?? 0) > 0
    ? 'He ran this turn: full auto only.'
    : 'He ran this turn: only full auto may fire, and this weapon has none.';
